// Corpo da listagem — os quatro estados: skeleton no formato real, erro com
// retry, vazio (com e sem filtro) e a lista. Só renderiza; quem decide é o painel.
import {
  CalendarClockIcon,
  PlusIcon,
  SearchXIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { EmptyState } from "@/components/commons/empty-state"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { PaymentConfiguration } from "@/types/payment-configurations"

import { SKELETON_ROWS } from "../constants"
import type { FeedbackVariant } from "../feedback"
import { PaymentConfigurationCard } from "./payment-configuration-card"
import { PaymentConfigurationFeedback } from "./payment-configuration-feedback"

const prefix = "pages.paymentConfigurations"

interface PaymentConfigurationsListProps {
  items: PaymentConfiguration[]
  isLoading: boolean
  isError: boolean
  /** Qual falha mostrar (acesso, permissão ou transitória). */
  errorVariant?: FeedbackVariant | null
  /** Mostrando a página anterior enquanto a nova carrega. */
  isStale?: boolean
  hasActiveFilters: boolean
  canWrite: boolean
  createHref: string
  togglingId: string | null
  whiteLabelNameOf: (whiteLabelId: string) => string
  detailHrefOf: (id: string) => string
  onRetry: () => void
  onClearFilters: () => void
  onActivate: (configuration: PaymentConfiguration) => void
  onDeactivate: (
    configuration: PaymentConfiguration
  ) => void
  className?: string
}

const ListSkeleton = () => (
  <div
    className="space-y-3"
    data-testid="payment-configurations-skeleton"
  >
    {Array.from({ length: SKELETON_ROWS }).map(
      (_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-xl border border-l-4 bg-card shadow-sm"
        >
          <div className="flex items-start justify-between gap-3 px-5 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-28" />
          </div>
          <div className="grid gap-4 border-t px-5 py-3 sm:grid-cols-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
      )
    )}
  </div>
)

export const PaymentConfigurationsList = ({
  items,
  isLoading,
  isError,
  errorVariant,
  isStale = false,
  hasActiveFilters,
  canWrite,
  createHref,
  togglingId,
  whiteLabelNameOf,
  detailHrefOf,
  onRetry,
  onClearFilters,
  onActivate,
  onDeactivate,
  className,
}: PaymentConfigurationsListProps) => {
  const { t } = useTranslation()

  if (isLoading) return <ListSkeleton />

  if (isError) {
    return (
      <PaymentConfigurationFeedback
        variant={errorVariant ?? "error"}
        onRetry={onRetry}
      />
    )
  }

  if (items.length === 0) {
    return hasActiveFilters ? (
      <EmptyState
        icon={SearchXIcon}
        title={t(`${prefix}.empty.filteredTitle`)}
        description={t(
          `${prefix}.empty.filteredDescription`
        )}
        action={
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onClearFilters}
          >
            {t(`${prefix}.filters.clear`)}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={CalendarClockIcon}
        title={t(`${prefix}.empty.title`)}
        description={t(`${prefix}.empty.description`)}
        action={
          canWrite && (
            <Button asChild size="sm" variant="outline">
              <Link to={createHref}>
                <PlusIcon
                  className="mr-1.5 h-3.5 w-3.5"
                  aria-hidden="true"
                />
                {t(`${prefix}.actions.create`)}
              </Link>
            </Button>
          )
        }
      />
    )
  }

  return (
    <ul
      aria-busy={isStale}
      className={cn(
        "space-y-3 motion-safe:transition-opacity",
        isStale && "opacity-60",
        className
      )}
    >
      {items.map((configuration) => (
        <li key={configuration.id}>
          <PaymentConfigurationCard
            configuration={configuration}
            whiteLabelName={whiteLabelNameOf(
              configuration.whiteLabelId
            )}
            detailHref={detailHrefOf(configuration.id)}
            canWrite={canWrite}
            isToggling={togglingId === configuration.id}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
          />
        </li>
      ))}
    </ul>
  )
}
